/**
 * light.js
 */

import { deepmerge } from '@mui/utils';
import { darken, alpha } from "@mui/material/styles";
import makeStyles from '@mui/styles/makeStyles';
import menuImage from 'assets/images/menu-background.jpg';
import constants from './constants';
import { generateBaseTheme, updateCommonTheme } from './common';


const theme = generateBaseTheme('light');


//#region PALETTE

theme.palette.isDarkMode = false;


// M3 system color role tokens
theme.palette.sys = {
  // primary
  primary:                  constants.palette.brandBlue.t700,
  onPrimary:                constants.palette.coolGrey.t25,
  onPrimaryVariant:         constants.palette.coolGrey.t200,
  primaryContainer:         constants.palette.brandBlue.t200,

  // secondary
  secondary:                constants.palette.brandOrange.t400,
  onSecondary:              constants.palette.brandBlue.t1000,
  onSecondaryVariant:       constants.palette.brandBlue.t800,
  secondaryContainer:       constants.palette.brandOrange.t300,

  // surface
  surface:                  constants.palette.coolGrey.t50,
  onSurface:                constants.palette.coolGrey.t600,
  onSurfaceVariant:         constants.palette.coolGrey.t400,

  // surface container
  // surfaceContainerLowest: 
  // surfaceContainerLow:
  surfaceContainer:         constants.palette.misc.white,
  surfaceContainerHigh:     constants.palette.coolGrey.t50,
  surfaceContainerHighest:  constants.palette.coolGrey.t100,

  // outline
  outline:                  constants.palette.coolGrey.t200,
  outlineVariant:           constants.palette.coolGrey.t100,
};


// PRIMARY
theme.palette.primary = {
  main:         theme.palette.sys.primary,
  light:        constants.palette.brandBlue.t500,
  dark:         constants.palette.brandBlue.t800,
  contrastText: theme.palette.sys.onPrimary,

  // legacy array
  colors: [
    constants.palette.brandBlue.t1000,
    constants.palette.brandBlue.t900,
    constants.palette.brandBlue.t800,
    constants.palette.brandBlue.t700,
    constants.palette.brandBlue.t600,
    constants.palette.brandBlue.t500,
    constants.palette.brandBlue.t400,
    constants.palette.brandBlue.t300,
    constants.palette.brandBlue.t200,
    constants.palette.brandBlue.t100,
    constants.palette.brandBlue.t50,
  ]
};


// SECONDARY
theme.palette.secondary = {
  main:         theme.palette.sys.secondary, // t400
  light:        constants.palette.brandOrange.t300,
  dark:         constants.palette.brandOrange.t500,
  contrastText: theme.palette.sys.onSecondary,
  
  // legacy array
  colors: [
    constants.palette.brandOrange.t900,
    constants.palette.brandOrange.t800,
    constants.palette.brandOrange.t700,
    constants.palette.brandOrange.t600,
    constants.palette.brandOrange.t500,
    constants.palette.brandOrange.t400,
    constants.palette.brandOrange.t300,
    constants.palette.brandOrange.t200,
    constants.palette.brandOrange.t100,
    constants.palette.brandOrange.t50,
  ]
};


// GREY
theme.palette.grey = {
  main:         constants.palette.coolGrey.t600,
  contrastText: constants.palette.coolGrey.t50,

  // 25:     constants.palette.coolGrey.t25, // remove
  50:     constants.palette.coolGrey.t50,
  100:    constants.palette.coolGrey.t100,
  200:    constants.palette.coolGrey.t200,
  300:    constants.palette.coolGrey.t300,
  400:    constants.palette.coolGrey.t400,
  500:    constants.palette.coolGrey.t500,
  600:    constants.palette.coolGrey.t600,
  700:    constants.palette.coolGrey.t700,
  800:    constants.palette.coolGrey.t800,
  900:    constants.palette.coolGrey.t900,
  A100:   constants.palette.coolGrey.t100,
  A200:   constants.palette.coolGrey.t200,
  A400:   constants.palette.coolGrey.t400,
  A700:   constants.palette.coolGrey.t700,
  
  // legacy array
  colors: [
    constants.palette.coolGrey.t900,
    constants.palette.coolGrey.t800,
    constants.palette.coolGrey.t700,
    constants.palette.coolGrey.t600,
    constants.palette.coolGrey.t500,
    constants.palette.coolGrey.t400,
    constants.palette.coolGrey.t300,
    constants.palette.coolGrey.t200,
    constants.palette.coolGrey.t100,
    constants.palette.coolGrey.t50,
    constants.palette.coolGrey.t25,
  ]
};


// ERROR
theme.palette.error = {
  main:         constants.palette.errorRed.t400,
  light:        constants.palette.errorRed.t200,
  dark:         constants.palette.errorRed.t600,
  contrastText: constants.palette.coolGrey.t50,
};


// WARNING
theme.palette.warning = {
  main:         constants.palette.warning.t300,
  light:        constants.palette.warning.t100,
  dark:         constants.palette.warning.t500,
  contrastText: constants.palette.coolGrey.t50,
}


// SUCCESS
theme.palette.success = {
  main:         constants.palette.successGreen.t300,
  light:        constants.palette.successGreen.t200,
  dark:         constants.palette.successGreen.t400,
  contrastText: constants.palette.coolGrey.t50,
};


// INFO
theme.palette.info = {
  main:         constants.palette.brandBlue.t400,
  light:        constants.palette.brandBlue.t200,
  dark:         constants.palette.brandBlue.t800,
  contrastText: constants.palette.coolGrey.t900,
}


// TEXT
theme.palette.text = {
  disabled: constants.palette.coolGrey.t300,
  hint:     constants.palette.coolGrey.t300,
}

// MISC
theme.palette.action.hoverOpacity = 0.04;
theme.palette.action.hover = alpha(constants.palette.coolGrey.t900, theme.palette.action.hoverOpacity);
theme.palette.action.disabled = alpha(theme.palette.sys.onSurface, 0.4);
theme.palette.action.disabledBackground = alpha(constants.palette.coolGrey.t900, 0.12);

// CUSTOM HOVER FUNC
theme.palette.action.hoverBackground = (baseColor) => {
  return darken(baseColor, theme.palette.action.hoverOpacity);
}


// COMPONENT-SPECIFIC DEFINITIONS
theme.palette.comp = {
  fadedBackground: `rgba(255,255,255,0.7)`,

  navBar: {
    background: constants.palette.brandBlue.t800,
    onSurfaceVariant: constants.palette.brandBlue.t400,
  }, 

  appBar: {
    background: theme.palette.sys.primary,
    button: {
      disabled: alpha(theme.palette.sys.onPrimary, 0.6),
    },
  },

  mainMenu: {
    background: constants.palette.brandBlue.t700,
    backgroundMobile: constants.palette.brandBlue.t700,
    image: menuImage
  },

  notifications: {
    chipText: constants.palette.coolGrey.t1000,
    toastBackground: constants.palette.brandBlue.t600,
    toastShadowParams: [10, constants.palette.coolGrey.t900, 1, [0.4, 0.35, 0.3]],
  },

  calendar: {
    dayBackground: constants.palette.coolGrey.t50,
    dayHighlightBackground: constants.palette.coolGrey.t100,
    trainingClassBadge: constants.palette.brandOrange.t700,
    vetVisitBadge: constants.palette.brandPuce.t500,
  },
  
  recordRow: {
    headerHover: (baseColor) => darken(baseColor, 0.12),
    event: {
      background: constants.palette.brandBlue.t500,
      backgroundNotAttending: constants.palette.brandBlue.t400,
    },
    deployment: {
      background: constants.palette.coolGrey.t400,
    },
    class: {
      background: constants.palette.brandOrange.t600,
    },
    vet: {
      background: constants.palette.brandPuce.t500,
    },
  },

  menuDrawer: {
    menuBackground: constants.palette.coolGrey.t100,
  },

  dataGrid: {
    headerBackground: constants.palette.coolGrey.t100,
    rowBackground: constants.palette.misc.white,
  },

  labeledSwitch: {
    offColor: constants.palette.coolGrey.t400,
    onColor: constants.palette.misc.white,
    borderColor: constants.palette.coolGrey.t400,
  },

  outdatedAlert: {
    removedBackground: constants.palette.errorRed.t200,
    addedBackground: constants.palette.successGreen.t200,
  },

  tip: {
    icon: constants.palette.brandOrange.t700,
  },

  // fade background under popper - mobile-only
  popover: {
    underBackground: alpha(constants.palette.coolGrey.t900, 0.15),
  },

  tracking: {
    currentLocationColor: constants.palette.brandBlue.t500,
    shadowParams : [2, constants.palette.coolGrey.t900, 1, [0.6, 0.55, 0.5]]
  },
}

//#endregion


//#region SHADOWS

theme.shadowParams = {};
theme.shadowParams.shadowColor = constants.palette.coolGrey.t900;
theme.shadowParams.fadeBase = [0.2, 0.14, 0.12];
theme.shadowParams.subtleShadowAmount = 0.05;

//#endregion


//#region UPDATE COMMON THEME

updateCommonTheme(theme);

//#endregion


//#region COMPONENTS

theme.components = deepmerge(theme.components, {
  MuiAutocomplete: {
    styleOverrides: {
      popper: {
        borderRadius: 4,
        boxShadow: theme.shadows[5]
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      root: {
        [theme.breakpoints.down('sm')]: {
          backgroundColor: theme.palette.comp.popover.underBackground
        }
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ ownerState }) => ({
        ...(ownerState.color === 'default' && ownerState.variant === 'filled' && {
          backgroundColor: constants.palette.coolGrey.t100,
        }),
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: theme.palette.sys.primary,
      },
      arrow: {
        color: theme.palette.sys.primary,
      }
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: {
        color: theme.palette.primary.colors[3],
      },
    },
  },  
});

//#endregion


//#region GLOBAL STYLES

let globalStyles = theme.globalStyles || {};
function addGlobalStyles(obj) {
  globalStyles = Object.assign(globalStyles, obj);
}
theme.GlobalStyles = function () {
  const styles = makeStyles({ '@global': globalStyles });
  styles();
  return null;
}
addGlobalStyles({
  // TEXT COLORS
  '.text-warning': {
    color: constants.palette.warning.t400,
  },
  '.text-error': {
    color: constants.palette.errorRed.t400
  },

  // GENERIC ICON COLORS
  '.icon-incomplete, .icon-upcoming, .icon-undecided, .icon-not-attending, .icon-unknown, .icon-disabled, .icon-not-reviewed': {
    color: constants.palette.coolGrey.t300
  },
  '.icon-complete, .icon-na, .icon-reviewed, .icon-accepted': {
    color: constants.palette.successGreen.t400
  },
  '.icon-error, .icon-rejected': {
    color: constants.palette.errorRed.t400
  },
  '.icon-outdated': {
    color: constants.palette.brandOrange.t600
  },
  '.icon-warning': {
    color: constants.palette.warning.t300
  },

  // CHIP COLORS
  '.chip-incomplete, .chip-undecided, .chip-not-attending, .chip-not-reviewed': {
    backgroundColor: constants.palette.coolGrey.t100,
  },
  '.chip-complete, .chip-reviewed': {
    backgroundColor: constants.palette.successGreen.t100,
  },
  '.chip-upcoming': {
    backgroundColor: constants.palette.brandBlue.t100,
  },
  '.chip-outdated': {
    backgroundColor: constants.palette.brandOrange.t200,
  },
  '.chip-ok': {
    backgroundColor: constants.palette.successGreen.t100,
  },
  '.chip-warning': {
    backgroundColor: constants.palette.brandOrange.t100,
  },
  '.chip-error': {
    backgroundColor: constants.palette.errorRed.t100
  },
  '.chip-font-incomplete, .chip-font-undecided, .chip-font-not-attending, .chip-font-not-reviewed': {
    color: constants.palette.coolGrey.t500
  },
  '.chip-font-complete, .chip-font-reviewed, .chip-font-ok': {
    color: constants.palette.successGreen.t700
  },
  '.chip-font-upcoming': {
    color: constants.palette.brandBlue.t800
  },
  '.chip-font-error': {
    color: constants.palette.errorRed.t500
  },
  '.chip-font-outdated': {
    color: constants.palette.warning.t500
  },
});

//#endregion


export default theme;